import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Heart, CheckCircle2 } from "lucide-react";

const TIMELINE = [
  { year: "Before", title: "The Nursing Years", body: "12-hour shifts, side hustles, and the first taste of building income outside a 9–5 in Texas." },
  { year: "Start", title: "First Arbitrage Rental", body: "Leased her first unit, furnished it out of pocket, and listed it on Airbnb — no property owned, just hustle." },
  { year: "Scale", title: "Cleaned Every Turnover Herself", body: "Perfect turnovers matter more than perfect furniture — so Kristy grabbed a caddy and did the flips herself." },
  { year: "Build", title: "Started Her Own Cleaning Company", body: "She couldn't find cleaners she trusted, so she trained her own team — and quickly started cleaning other hosts' properties across San Antonio." },
  { year: "Grow", title: "Became the Property Manager", body: "Owners in San Antonio kept asking for help — she rolled the cleaning company into full-service property management, the co-hosting business she still runs today." },
  { year: "Teach", title: "BNB Co-Host Academy", body: "Started teaching the exact arbitrage → clean → PM playbook to hosts nationwide — the birth of Host Hustle™." },
  { year: "Now", title: "Host Hustle™", body: "The flagship education platform — Host Hustle. Smarter hosting. Higher returns. Everywhere Texas and beyond." },
];

export default function About() {
  return (
    <div className="bg-white">
      <Navbar />
      <section className="py-24 max-w-6xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-3xl mx-auto">
          <div className="uppercase tracking-[0.28em] text-xs text-brand-pink mb-4">The Story</div>
          <h1 className="font-display text-5xl md:text-6xl leading-tight">Meet <span className="font-script text-brand-pink text-6xl md:text-7xl">Kristy</span></h1>
          <p className="mt-8 text-neutral-600 text-lg leading-relaxed">
            Former nurse. Entrepreneur. Property manager. Cleaning-company builder. Started with short-term rental arbitrage in Texas, cleaned her own units, built her own cleaning company, and rolled it all into a full-service property management business across San Antonio — now she teaches you exactly how to do it.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-10 mt-20 items-start">
          <div className="lg:col-span-5 sticky top-28">
            <img
              src="/kristy-hero.jpg"
              alt="Kristy at her Host Hustle desk"
              className="rounded-[2rem] w-full aspect-[4/5] object-cover"
            />
            <div className="mt-6 rounded-3xl bg-brand-blush/60 p-6">
              <div className="font-script text-2xl text-brand-pink mb-2">A note from Kristy</div>
              <p className="text-sm text-neutral-700 leading-relaxed">
                "I don't teach theory. I teach the plays that put money in your account — the same ones I used when I was starting with a single unit and a big idea."
              </p>
            </div>
          </div>

          <div className="lg:col-span-7 space-y-10">
            {TIMELINE.map((t, i) => (
              <div key={t.year} data-testid={`timeline-${i}`} className="grid grid-cols-[80px_1fr] gap-6">
                <div className="text-right">
                  <div className="font-display text-2xl text-brand-pink">{t.year}</div>
                </div>
                <div className="pl-6 border-l border-neutral-200">
                  <h3 className="font-display text-2xl mb-2">{t.title}</h3>
                  <p className="text-neutral-600 leading-relaxed">{t.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-brand-blush/40">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="uppercase tracking-[0.28em] text-xs text-brand-pink mb-3">What Kristy Believes</div>
          <h2 className="font-display text-4xl md:text-5xl mb-10">Hosting is a <span className="italic">business</span> — not a hobby.</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              "Systems beat hustle.",
              "Owners want peace, not pitches.",
              "Freedom is built one guest at a time.",
            ].map((v, i) => (
              <div key={i} className="rounded-3xl bg-white p-8 flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-brand-pink/15 flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="w-5 h-5 text-brand-pink" />
                </div>
                <div className="text-left font-display text-xl leading-snug">{v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
